import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProduct, HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { AvailabilityDto, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import { toToolkitApplicationState } from "@store/Framework/Store/ApplicationState";
import loadSubstitutes from "@store/Framework/Store/Store/Components/SubstituteProducts/Handlers/LoadSubstitutes";
import setSubstituteProductIds from "@store/Framework/Store/Store/Components/SubstituteProducts/Handlers/SetSubstituteProductIds";
import Carousel, { CarouselPresentationProps } from "@store/Overrides/Components/Carousel";
import { productCardProps } from "@store/Themes/common/props/productCard";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Store Custom Code - Start */
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@store/Overrides/Components/ProductCarouselProduct";
import useOnDemandInventory from "@store/Framework/Common/Hooks/useOnDemandInventory";
/* Store Custom Code - End */

type Props = WidgetProps &
    HasProduct &
    HasProductContext &
    HasParentProductId &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps & HasProductContext & HasParentProductId) => {
    const { userName } = state.context.session;
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const carouselId = `${ownProps.id}-substitute-products`;

    return {
        isSignedIn: userName && !currentUserIsGuest,
        productIds: toToolkitApplicationState(state).store.components.substituteProducts.productsIds,
        productInfoLists: state.components.productInfoLists.productInfoListById[carouselId],
        substituteProducts: getProductsForProductInfoList(state, carouselId),
        parentProductState: getProductState(state, ownProps.parentProductId),
        parentProductInfo: state?.pages?.productDetails?.productInfosById
            ? state.pages.productDetails.productInfosById[ownProps.parentProductId]
            : undefined,
        carouselId,
    };
};

const mapDispatchToProps = {
    loadProduct,
    loadSubstitutes,
    setSubstituteProductIds,
};

export interface productDetailsSubstitutesStyles {
    carouselProductStyles?: ProductCarouselProductStyles;
    carousel?: CarouselPresentationProps;
    carouselSlideInner?: InjectableCss;
    modalContentOne?: TypographyPresentationProps;
    modalContentTwo?: TypographyPresentationProps;
}

export const productDetailsSubstitutesStyles: productDetailsSubstitutesStyles = {
    modalContentTwo: {
        css: css`
            margin-bottom: ${pxToRem(25)};
        `,
    },
    carouselSlideInner: productCardProps.card,
};

const styles = productDetailsSubstitutesStyles;

const ProductDetailsSubstitutes = ({
    productContext: { product, productInfo },
    parentProductId,
    productIds,
    loadSubstitutes,
    setSubstituteProductIds,
    isSignedIn,
    substituteProducts,
    parentProductState,
    parentProductInfo,
    carouselId,
}: Props) => {
    const [lastProductDisplayed, setLastProductDisplayed] = useState("");
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);

    let productToUse = product;
    let productInfoToUse = productInfo;

    if (
        !product.properties.erpSubstituteSku &&
        parentProductId !== product.id &&
        parentProductState?.value?.properties &&
        parentProductState?.value?.properties.erpSubstituteSku &&
        parentProductState?.value?.properties.erpSubstituteSku !== ""
    ) {
        productToUse = parentProductState?.value;
        productInfoToUse = parentProductInfo || productInfo;
    }

    const { erpSubstituteSku, offerSubstitudeProductsAllUser, offerSubstitudeProductsGuestOnly, isDiscontinuedCustom } =
        productToUse.properties;
    const skusArray: string[] = erpSubstituteSku ? erpSubstituteSku.split(",") : [];

    // Use on-demand inventory (shared single API call) for accurate availability
    const onDemandInventory = useOnDemandInventory(product.id);

    const currentProductAvailability: AvailabilityDto | undefined =
        onDemandInventory?.inventoryAvailabilityDtos?.find(
            o => o.unitOfMeasure.toLowerCase() === (product?.unitOfMeasures?.find(x => x.unitOfMeasure.toLowerCase()) || ""),
        )?.availability || undefined;

    const substituteProductAvailability: AvailabilityDto | undefined =
        productInfoToUse.inventory?.inventoryAvailabilityDtos?.find(
            o => o.unitOfMeasure.toLowerCase() === (productInfoToUse.unitOfMeasure?.toLowerCase() || ""),
        )?.availability || undefined;

    useEffect(() => {
        return () => {
            setLastProductDisplayed("");
        };
    }, []);

    useEffect(() => {
        if (skusArray.length > 0) {
            loadSubstitutes({ id: carouselId, productIds: skusArray });
        }
    }, [erpSubstituteSku]);

    useEffect(() => {
        if (productIds.length < 1 && erpSubstituteSku) {
            setSubstituteProductIds({ productIds: skusArray });
        }
    }, [erpSubstituteSku, productIds]);

    useEffect(() => {
        if (product.productNumber !== lastProductDisplayed) {
            if (substituteProducts && substituteProducts.length > 0 && showModal) {
                setModalIsOpen(true);
                setLastProductDisplayed(product.productNumber);
            }

            if (
                productToUse.isDiscontinued ||
                isDiscontinuedCustom?.toLowerCase() === "true" ||
                (currentProductAvailability?.messageType === 2 && substituteProductAvailability?.messageType === 1)
            ) {
                if (substituteProducts && substituteProducts.length > 0) {
                    if (
                        (offerSubstitudeProductsAllUser &&
                            isSignedIn &&
                            offerSubstitudeProductsAllUser?.toLowerCase() === "true") ||
                        (offerSubstitudeProductsGuestOnly &&
                            !isSignedIn &&
                            offerSubstitudeProductsGuestOnly?.toLowerCase() === "true") ||
                        (offerSubstitudeProductsAllUser &&
                            !isSignedIn &&
                            offerSubstitudeProductsAllUser?.toLowerCase() === "true")
                    ) {
                        setShowModal(true);
                    } else {
                        setShowModal(false);
                    }
                } else {
                    setShowModal(false);
                }
            } else {
                setShowModal(false);
            }
        }
    }, [substituteProducts, product.id, currentProductAvailability]);

    if (!erpSubstituteSku || !substituteProducts || substituteProducts.length === 0) {
        return null;
    }

    if (!showModal) {
        return null;
    }

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const max = 3;
    const maxItems = substituteProducts.length < max ? substituteProducts.length : max;
    const modalStyles: ModalPresentationProps = {
        sizeVariant: substituteProducts.length === 1 ? "small" : "medium",
    };

    return (
        <Modal
            {...modalStyles}
            headline={translate("We are sorry")}
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
        >
            <Typography as="p" {...styles.modalContentOne}>
                {translate("The product you are looking for is no longer in stock.")}
            </Typography>
            <Typography as="p" {...styles.modalContentTwo}>
                {translate("However, our experts offer you this or these comparable product(s).")}
            </Typography>
            {substituteProducts.length && (
                <Carousel
                    {...styles.carousel}
                    id="product-carousel-substitutes"
                    maxNumberOfColumns={maxItems}
                    showDots={true}
                    slides={substituteProducts.map(product => ({
                        id: product.id,
                        renderComponent: () => {
                            return (
                                <StyledWrapper
                                    {...styles.carouselSlideInner}
                                    id={`product_${product.id}`}
                                    data-test-key={product.id}
                                    data-test-selector="carousel_slide"
                                >
                                    <ProductCarouselProduct
                                        carouselId={carouselId}
                                        product={product}
                                        showImage={true}
                                        showBrand={true}
                                        showTitle={true}
                                        showPartNumbers={true}
                                        showPrice={false}
                                        showAddToCart={true}
                                        showAddToList={false}
                                        showImageBadges={true}
                                        maximumNumberOfImageBadges={3}
                                        showTextBadges={false}
                                        maximumNumberOfTextBadges={3}
                                        extendedStyles={styles.carouselProductStyles}
                                        showAvailability={true}
                                        showAttributes={false}
                                        isSubstitute={true /* Store Custom Code */}
                                    />
                                </StyledWrapper>
                            );
                        },
                    }))}
                />
            )}
        </Modal>
    );
};
const widgetModule: WidgetModule = {
    component: withParentProductId(
        withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsSubstitutes)),
    ),
    definition: {
        displayName: "Product Substitutes Modal",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};
export default widgetModule;
